import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Breadcrumbs, I18N, Loader } from '@/components';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { LicenseForm } from '@/forms';
import { ApiException, useApi } from '@/hooks/useApi';
import { differenceInDays, startOfDay } from 'date-fns';
import dayjs from 'dayjs';
const LicenseCreationPage = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { callApi } = useApi();
    const { showError, showSuccess } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const handleSubmit = useCallback(async (data) => {
        setLoading(true);
        try {
            const expirationDays = data.expiration
                ? differenceInDays(startOfDay(new Date(data.expiration)), startOfDay(new Date()))
                : 0;
            const egressData = data.egressSeats
                ? [{ egressPluginId: 'purevpn', limit: data.egressSeats * 5 }]
                : [];
            const payload = {
                targetEntitlement: {
                    customerId: data.customerId,
                    sku: data.licenseType,
                    licenseType: data.seatType,
                    seats: Number(data.seats),
                    expiration: expirationDays,
                    enabled: true,
                    egressPlugins: egressData,
                    numActivations: Number(data.activationLimit),
                    entitlementMetadata: {
                        name: data.name || null,
                        notes: data.notes || null,
                        trialType: data.trialType || null,
                        hubspotDealId: data.hubspotDealId || null,
                        activationLimit: data.activationLimit
                            ? Number(data.activationLimit)
                            : null,
                    },
                },
            };
            const response = await callApi('POST', '/licenses', payload);
            if (response &&
                typeof response === 'object' &&
                'entitlementId' in response) {
                showSuccess('License created successfully!');
                navigate(`/licenses/${response.entitlementId}`, { replace: true });
            }
        }
        catch (e) {
            const detail = e instanceof ApiException && e.detail;
            showError(detail || 'licenses.messages.createError');
        }
        finally {
            setLoading(false);
        }
    }, [callApi, navigate, showError, showSuccess]);
    return (_jsxs("div", { className: "p-2 max-w-[800px] mx-auto", children: [_jsxs(Breadcrumbs, { children: [_jsx(Link, { replace: true, to: "/licenses", children: _jsx(I18N, { children: "navigation.licenses" }) }), _jsx(Link, { replace: true, to: "/licenses/new", children: _jsx(I18N, { children: "common.new" }) })] }), _jsxs("div", { id: "license-creation-page", children: [_jsx(LicenseForm, { defaultValues: {
                            owner: params.get('ownerId') ?? null,
                            company: params.get('customerId') ?? null,
                            hubspotDealId: '',
                            name: '',
                            notes: '',
                            licenseType: '',
                            seatType: '',
                            seats: '',
                            activationLimit: '',
                            trialType: '',
                            egressSeats: '',
                            expiration: dayjs().add(1, 'year'),
                        }, onSubmit: handleSubmit, onCancel: () => {
                            navigate(`/licenses?${params.toString()}`, { replace: true });
                        }, mode: "onChange", submitting: loading, children: _jsx(I18N, { children: "licenses.newLicense" }) }), _jsx(Loader, { loading: loading })] })] }));
};
export default LicenseCreationPage;
